import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { SESSION_DURATION_MS } from "../constants/chat";
import "../styles/SessionBanner.css";

type SessionBannerProps = {
  menu?: React.ReactNode;
};

function formatMs(ms: number) {
  const clamped = Math.max(0, ms);
  const totalSeconds = Math.floor(clamped / 1000);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");

  return `${hh}:${mm}:${ss}`;
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

export default function SessionBanner({ menu }: SessionBannerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const [timeLeftMs, setTimeLeftMs] = useState<number>(() => {
    const start = Number(sessionStorage.getItem("sessionStartTime") || "0");
    return SESSION_DURATION_MS - (Date.now() - start);
  });

  const [hasStart, setHasStart] = useState<boolean>(() => {
    return !!sessionStorage.getItem("sessionStartTime");
  });

  const recompute = () => {
    const startRaw = sessionStorage.getItem("sessionStartTime");
    setHasStart(!!startRaw);

    const start = Number(startRaw || "0");
    const left = SESSION_DURATION_MS - (Date.now() - start);
    setTimeLeftMs(left);
  };

  useEffect(() => {
    const interval = setInterval(recompute, 250);

    const onSessionUpdated = () => recompute();
    window.addEventListener("idless:session-updated", onSessionUpdated);

    return () => {
      clearInterval(interval);
      window.removeEventListener("idless:session-updated", onSessionUpdated);
    };
  }, []);

  useLayoutEffect(() => {
    const updateBannerHeight = () => {
      const banner = ref.current;
      if (banner) {
        const height = banner.getBoundingClientRect().height;
        document.documentElement.style.setProperty(
          "--session-banner-height",
          `${height}px`,
        );
      } else {
        document.documentElement.style.setProperty(
          "--session-banner-height",
          "0px",
        );
      }
    };

    updateBannerHeight();

    requestAnimationFrame(() => {
      updateBannerHeight();
      requestAnimationFrame(updateBannerHeight);
    });

    const handleResize = () => {
      updateBannerHeight();
    };

    window.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener("resize", handleResize);

    const timeout = setTimeout(updateBannerHeight, 150);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("resize", handleResize);
      clearTimeout(timeout);
    };
  }, [hasStart]);

  if (location.pathname === "/session-expired") return null;
  if (!hasStart) return null;

  const isExpired = timeLeftMs <= 0;

  return (
    <div
      ref={ref}
      className="session-banner"
      style={{
        position: "fixed",
        top: "var(--visual-viewport-offset-top, 0px)",
        left: 0,
        right: 0,
        zIndex: 9999,
        width: "100%",
        background: isExpired ? "#ffe5e5" : "#ffffff",
        display: "flex",
        justifyContent: "center",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "420px",
          padding: "10px 12px",
          fontSize: 14,
          display: "grid",
          gridTemplateColumns: "36px 1fr auto",
          alignItems: "center",
          gap: 8,
        }}
      >
        <img
          src="/idlesslogo.png"
          alt="IDless Logo"
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "8px",
            objectFit: "cover",
            display: "block",
          }}
        />

        <div className="session-banner-timer" aria-live="off">
          {!isExpired ? (
            <>
              <ClockIcon />
              <b>{formatMs(timeLeftMs)}</b>
            </>
          ) : (
            <span className="session-banner-expired">Expired</span>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          {menu}
        </div>
      </div>
    </div>
  );
}
